/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "./types";
import { formatQty } from "./formatQty";

type History = {
  movements: {
    id: number;
    type: string;
    qty_delta: number;
    total_cost: number;
    occurred_at: string;
    detail: string | null;
  }[];
  used_in: { entity_type: "product" | "extra"; entity_id: number; name: string | null }[];
};

type Props = { item: StockItem; onClose: () => void };

// Where an item's stock came from and went: receipts, sales, waste, counts —
// and which dishes use it.
export const StockItemHistoryModal = defineComponent<Props>({
  name: "StockItemHistoryModal",
  props: ["item", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const loading = ref(true);
    const history = ref<History | null>(null);

    onMounted(async () => {
      try {
        history.value = (await trpc.stock.history.query({ id: props.item.id, limit: 50 })) as History;
      } catch {
        toast.showToast(t("inventory.stock.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    const detail = (m: History["movements"][number]) =>
      m.type === "waste" && m.detail ? t(`inventory.waste.reason_${m.detail}`) : (m.detail ?? "");

    return () => (
      <LemmonModal
        title={props.item.name}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4 text-right">
            <button class="btn btn-neutral" onClick={() => props.onClose()}>
              {t("inventory.purchases.close")}
            </button>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <div class="flex flex-wrap gap-4 text-sm">
            <div>
              <div class="text-xs text-base-content/60">{t("inventory.stock.col_on_hand")}</div>
              <div class="font-semibold">
                {formatQty(props.item.qty_on_hand, props.item.base_unit, props.item.units)}
              </div>
            </div>
            <div>
              <div class="text-xs text-base-content/60">{t("inventory.stock.value")}</div>
              <div class="font-semibold">
                {(Math.max(props.item.qty_on_hand, 0) * props.item.avg_cost).toFixed(2)}
              </div>
            </div>
          </div>

          {loading.value || !history.value ? (
            <div class="flex justify-center p-6">
              <Loading />
            </div>
          ) : (
            <>
              <div>
                <div class="mb-1 text-sm font-medium">{t("inventory.stock.used_in")}</div>
                {history.value.used_in.length === 0 ? (
                  <p class="text-sm text-base-content/50">{t("inventory.stock.not_used")}</p>
                ) : (
                  <div class="flex flex-wrap gap-1">
                    {history.value.used_in.map((u) => (
                      <span key={`${u.entity_type}:${u.entity_id}`} class="badge badge-ghost">
                        {u.name ?? `#${u.entity_id}`}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div class="mb-1 text-sm font-medium">{t("inventory.stock.movements")}</div>
                {history.value.movements.length === 0 ? (
                  <p class="text-sm text-base-content/50">{t("inventory.stock.no_movements")}</p>
                ) : (
                  <div class="max-h-80 overflow-auto">
                    <table class="table table-sm">
                      <tbody>
                        {history.value.movements.map((m) => (
                          <tr key={m.id}>
                            <td class="whitespace-nowrap text-xs text-base-content/60">
                              {new Date(m.occurred_at).toLocaleString()}
                            </td>
                            <td>{t(`inventory.stock.movement_${m.type}`)}</td>
                            <td class="text-base-content/70">{detail(m)}</td>
                            <td
                              class={[
                                "whitespace-nowrap text-right",
                                m.qty_delta < 0 ? "text-error" : "text-success",
                              ].join(" ")}
                            >
                              {`${m.qty_delta > 0 ? "+" : ""}${m.qty_delta} ${props.item.base_unit}`}
                            </td>
                            <td class="whitespace-nowrap text-right text-sm">{m.total_cost.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </LemmonModal>
    );
  },
});
